import {
	Box,
	IconButton,
	Stack,
	SvgIcon,
	SxProps,
	Theme,
	Link,
	keyframes,
	Tooltip,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import PersonIcon from "@mui/icons-material/Person";
import EngieLogo from "../../common/assets/icons/logo.svg";
import { grey, neutralPrimary, primaryColor, white } from "../../core/theme";
import {
	selectAuthenticatedUser,
	selectIsAuthenticated,
	selectUserGroupNames,
	selectUserGroups,
	selectWsConnected,
} from "../../features/authentication/auth.selector";
import { useDispatch, useSelector } from "react-redux";
import { selectisNewNotificationReceivedFlag } from "../../features/notifications/notifications.selector";
import { setNotificationsDrawerOpened } from "../../features/notifications/notificationsSlice";
import PowerIcon from "@mui/icons-material/Power";
import PowerOffIcon from "@mui/icons-material/PowerOff";
import { PATH } from "../../router-path";
import { useNavigate } from "react-router-dom";
import { VpnStatus } from "./VpnStatus";
import { useVpnConnectionCheck } from "../hooks/useVpnConnectionCheck";
import { PropsWithChildren } from "react";
import { setProfileOpened } from "../../features/authentication/authSlice";

const belring = keyframes`
  33% {
    transform: rotate(-30deg);
  }
  66% {
    transform: rotate(30deg);
  }
  99% {
    transform: rotate(0deg);
  }
`;

const style: { [key: string]: SxProps<Theme> } = {
	headerContainer: {
		display: "flex",
		padding: "35px 40px 0",
		borderBottom: `1px solid ${grey}`,
		marginBottom: "40px",
	},
	prod: {
		bgcolor: white,
	},
	dev: {
		background:
			"linear-gradient(180deg, rgba(165,207,182,1) 0%, rgba(255,255,255,1) 100%, rgba(255,255,255,1) 100%);",
	},
	qa: {
		background:
			"linear-gradient(180deg, rgba(98,223,231,1) 0%, rgba(255,255,255,1) 100%, rgba(255,255,255,1) 100%);",
	},
	headerRight: {
		flexGrow: 2,
		display: "flex",
		alignItems: "center",
		justifyContent: "end",
		"& > *": {
			marginLeft: "64px !important",
			height: 40,
		},
		"& > div": {
			marginLeft: "10px !important",
		},
		"& .selected": {
			borderBottom: `2px ${primaryColor} solid`,
		},
	},
	login: {
		fontSize: 14,
		weight: 700,
		color: "#007ACD",
	},
	role: {
		fontSize: 12,
		weight: 400,
		color: "#778C9B",
		overflow: "hidden",
    	whiteSpace: "nowrap",
    	textOverflow: "ellipsis",
    	width: "300px",
    	display: "inline-block",		
	},
	navTitle: {
		textDecoration: "none",
		color: "inherit",
		"&:hover": {
			cursor: "pointer",
			opacity: 0.7,
			textDecoration: "none",
		},
		"&.selected": {
			textDecoration: "none",
		},
	},
	newNotification: {
		color: "red",
		animation: `${belring} 5s infinite ease-in-out`,
	},
	logoContainer: {
		display: "flex",
		minHeight: "50px",
		size: "20px",
	},
};

function HeaderLink(props: PropsWithChildren<{ link: string }>) {
	const navigate = useNavigate();
	const { link } = props;
	return (
		<Link
			variant="h3"
			href={link}
			sx={style.navTitle}
			className={window.location.pathname == link ? "selected" : ""}
			onClick={(e) => {
				e.preventDefault();
				navigate(link);
				return false;
			}}
		>
			{props.children}
		</Link>
	);
}

function Header() {
	const navigate = useNavigate();

	const user = useSelector(selectAuthenticatedUser);
	const groups = useSelector(selectUserGroupNames);
	const connected = useSelector(selectWsConnected);
	const { isVpnConnected } = useVpnConnectionCheck();
	const dispatch = useDispatch();

	const isAuthenticated = useSelector(selectIsAuthenticated);
	const currentUserGroup = useSelector(selectUserGroups);
	const isNewNotificationReceived = useSelector(
		selectisNewNotificationReceivedFlag
	);

	const env = import.meta.env.VITE_APP_ENV ?? "prod";
	const spreadSxProp = (s: SxProps<Theme>) => {
		return [...(Array.isArray(s) ? s : [s])];
	};
	const local = Intl.DateTimeFormat().resolvedOptions().timeZone;

	return (
		<Box
			sx={[
				...spreadSxProp(style.headerContainer),
				...spreadSxProp(style[env]),
			]}
		>
			<Box sx={style.logoContainer}>
				<Link
					href={PATH.LANDING}
					onClick={(e) => {
						e.preventDefault();
						navigate(PATH.LANDING);
						return false;
					}}
				>
					<SvgIcon
						sx={{ width: 88, height: 32, marginRight: 1 }}
						component={EngieLogo}
						inheritViewBox
					/>
				</Link>
				{connected && (
					<Tooltip title={"Connected"} placement="bottom-start">
						<PowerIcon color={"success"} sx={style.logoContainer} />
					</Tooltip>
				)}
				{!connected && (
					<Tooltip title={"Offline"} placement="bottom-start">
						<PowerOffIcon
							color={"error"}
							sx={style.logoContainer}
						/>
					</Tooltip>
				)}

				<Box sx={{ display: "flex", alignItems: "center" }}>
					<VpnStatus isVpnActif={isVpnConnected} />
				</Box>
			</Box>
			<Box sx={style.headerRight}>
				<HeaderLink link={PATH.TENDERS}>My&nbsp;Tenders</HeaderLink>
				{currentUserGroup.isTrader && (
					<>
						<HeaderLink link={PATH.RESPY_CALIBRATION_MANAGEMENT}>
							Respy Calibrations
						</HeaderLink>
						<HeaderLink link={PATH.PARAMETERS}>
							Pricing Param
						</HeaderLink>
						<HeaderLink link={PATH.PRICING_TRADERS}>
							Pricing traders
						</HeaderLink>
					</>
				)}
				<HeaderLink link={PATH.CONTRACTS}>My&nbsp;Contracts</HeaderLink>
				<HeaderLink link={PATH.SITES}>My&nbsp;Sites</HeaderLink>
				<HeaderLink link={PATH.REPORTS}>GEODE&nbsp;tool</HeaderLink>
				<HeaderLink link={PATH.DATA_QUALITY_REPORTS}>
					Data&nbsp;Quality&nbsp;Reporting
				</HeaderLink>
				<IconButton
					onClick={() => {
						dispatch(
							setNotificationsDrawerOpened({ opened: true })
						);
					}}
				>
					<NotificationsIcon
						color="primary"
						sx={
							isNewNotificationReceived
								? style.newNotification
								: null
						}
					/>
				</IconButton>
				<IconButton
					onClick={() => {
						dispatch(
							setProfileOpened({ opened: true })
						);
					}}
					sx={{
						marginLeft: "-10px !important"
					}}					
				>
					<PersonIcon
						color="primary"
					/>
				</IconButton>
				{isAuthenticated && (
					<Stack spacing={0}>
						<Box component="span" sx={style.login}>
							{user?.name}&nbsp;&nbsp;
							{user && (
								<Box
									component="span"
									color={neutralPrimary}
								>{`(${local})`}</Box>
							)}
						</Box>
						<Box component="span" sx={style.role} title={groups.join(", ")}>
							{groups.join(", ")}
						</Box>
					</Stack>
				)}
			</Box>
		</Box>
	);
}

export default Header;
